import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Stethoscope, FileText } from "lucide-react";
import { DoctorFormModal } from "@/components/medicos/DoctorFormModal";
import { UpgradeModal } from "@/components/plan/PlanGating";
import { usePlan } from "@/store/PlanContext";
import { useClinic } from "@/store/ClinicContext";
import { generateDoctorReport } from "@/utils/receipt";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

export const DoctorsAdmin = ({ doctors, setDoctors }) => {
  const { appointments } = useClinic();
  const { isMax } = usePlan();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const statsFor = (doc) => {
    const mine = appointments.filter((a) => a.doctorId === doc.id && a.status !== "cancelado");
    const pacientesUnicos = new Set(mine.map((a) => a.patient)).size;
    const faturado = mine.reduce((s, a) => s + (a.value || 0), 0);
    const recebido = mine.filter((a) => a.paid).reduce((s, a) => s + (a.received || a.value || 0), 0);
    const periodoMeses = 6;
    const mediaMensal = Math.round((mine.length / periodoMeses) * 10) / 10;
    return { totalAtendimentos: mine.length, pacientesUnicos, faturado, recebido, periodoMeses, mediaMensal };
  };

  const gerarRelatorio = (doc) => {
    generateDoctorReport(doc, statsFor(doc));
    toast.success("Relatório de atuação gerado", { description: `${doc.name} · PDF exportado.` });
  };

  const openNew = () => {
    if (!isMax && doctors.length >= 2) {
      setUpgradeOpen(true);
      return;
    }
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (doc) => {
    setEditing(doc);
    setModalOpen(true);
  };

  const save = (data) => {
    if (editing) {
      setDoctors((prev) => prev.map((d) => (d.id === editing.id ? { ...d, ...data } : d)));
      toast.success("Médico atualizado!", { description: `${data.name} · repasse ${data.repasse}%` });
    } else {
      const id = data.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") + "-" + Date.now().toString(36).slice(-4);
      setDoctors((prev) => [...prev, { ...data, id }]);
      toast.success("Médico cadastrado!", { description: `${data.name} · ${data.specialty}` });
    }
  };

  const remove = (doc) => {
    setDoctors((prev) => prev.filter((d) => d.id !== doc.id));
    toast.success("Médico removido", { description: doc.name });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-base font-bold text-slate-900">Corpo Clínico</h2>
          <p className="text-xs text-slate-500">
            {doctors.length} médico{doctors.length !== 1 ? "s" : ""} cadastrado
            {doctors.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          data-testid="new-doctor-btn"
          onClick={openNew}
          className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_2px_8px_rgba(37,99,235,0.3)] transition-colors hover:bg-blue-700"
        >
          <UserPlus className="h-4 w-4" />
          Novo Médico
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="hidden grid-cols-[1.6fr_1fr_1.4fr_1fr_130px] gap-4 border-b border-slate-100 bg-slate-50/60 px-6 py-3 lg:grid">
          {["Médico", "CRM / UF", "Especialidade CFM", "Repasse", "Ações"].map((h, i) => (
            <span
              key={h}
              className={`text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${
                i === 4 ? "text-right" : ""
              }`}
            >
              {h}
            </span>
          ))}
        </div>

        <div className="divide-y divide-slate-100">
          {doctors.map((d, i) => (
            <motion.div
              key={d.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25, delay: i * 0.04 }}
              data-testid={`doctor-row-${d.id}`}
              className="grid grid-cols-1 gap-3 px-6 py-4 transition-colors hover:bg-slate-50/80 lg:grid-cols-[1.6fr_1fr_1.4fr_1fr_130px] lg:items-center"
            >
              <div className="flex items-center gap-3">
                {d.avatar ? (
                  <img src={d.avatar} alt={d.name} className="h-9 w-9 rounded-full object-cover ring-1 ring-slate-200" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 ring-1 ring-blue-100">
                    <Stethoscope className="h-4 w-4 text-blue-600" />
                  </div>
                )}
                <span className="text-sm font-semibold text-slate-900">{d.name}</span>
              </div>
              <div className="font-mono-num text-sm text-slate-600">
                {d.crm}-{d.uf}
              </div>
              <div>
                <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  {d.specialty}
                </span>
              </div>
              <div>
                <span className="font-mono-num text-sm font-bold text-emerald-600">{d.repasse}%</span>
              </div>
              <div className="flex items-center gap-2 lg:justify-end">
                <button
                  data-testid={`edit-doctor-${d.id}`}
                  onClick={() => openEdit(d)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-blue-600"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`delete-doctor-${d.id}`}
                  onClick={() => setToDelete(d)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <DoctorFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        initial={editing}
        onSave={save}
      />

      <UpgradeModal
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        title="Limite de médicos atingido no Plano Basic"
        message="O Plano Basic permite até 2 médicos. Faça o upgrade para o Max para cadastrar profissionais sem limite."
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir médico?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação removerá <strong>{toDelete?.name}</strong> do corpo clínico e não pode ser
              desfeita. Gere o relatório de atuação em PDF antes de remover, se desejar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {toDelete && (
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-xs text-slate-600">
              <div className="grid grid-cols-2 gap-2">
                <span>Pacientes atendidos: <b className="text-slate-900">{statsFor(toDelete).pacientesUnicos}</b></span>
                <span>Atendimentos: <b className="text-slate-900">{statsFor(toDelete).totalAtendimentos}</b></span>
                <span>Média mensal: <b className="text-slate-900">{statsFor(toDelete).mediaMensal}/mês</b></span>
                <span>Faturado: <b className="text-slate-900">R$ {statsFor(toDelete).faturado.toLocaleString("pt-BR")}</b></span>
              </div>
              <button
                data-testid="report-doctor-btn"
                onClick={() => gerarRelatorio(toDelete)}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100"
              >
                <FileText className="h-3.5 w-3.5" />
                Baixar relatório de atuação (PDF)
              </button>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-btn"
              onClick={() => {
                remove(toDelete);
                setToDelete(null);
              }}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
